
import { supabase } from "@/integrations/supabase/client";

/**
 * Generate and save bot word for a round
 */
export const generateAndSaveBotWord = async (
  gameId: string,
  currentRoundNumber: number,
  userWord: string,
  botWord: string,
  usedWordsArray: string[],
  getAiResponseFn: any
) => {
  try {
    // Generate the bot's word using Claude
    const { nextAiWord, explanation } = await getAiResponseFn(userWord, botWord, usedWordsArray);
    
    // Update the current round with the generated word and explanation
    const { error: updateError } = await supabase
      .from("rounds")
      .update({ 
        ai_word: nextAiWord,
        ai_explanation: explanation
      })
      .eq("game_id", gameId)
      .eq("round_number", currentRoundNumber);
      
    if (updateError) throw updateError;
    
    return { 
      nextAiWord,
      explanation
    };
  } catch (error) {
    console.error("Error generating bot word:", error);
    throw error;
  }
};

/**
 * Create a new round
 */
export const createNewRound = async (gameId: string, roundNumber: number) => {
  try {
    const { error } = await supabase
      .from("rounds")
      .insert([{ 
        game_id: gameId, 
        round_number: roundNumber
      }]);
      
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error creating new round:", error);
    throw error;
  }
};

/**
 * Update game status to completed
 */
export const completeGame = async (gameId: string, finalWord: string) => {
  try {
    const { error } = await supabase
      .from("games")
      .update({ 
        status: "completed",
        final_word: finalWord
      })
      .eq("id", gameId);
      
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error completing game:", error);
    throw error;
  }
};

/**
 * Update game status to diverged (reached max rounds without convergence)
 */
export const divergeGame = async (gameId: string) => {
  try {
    console.log("Marking game as diverged, gameId:", gameId);
    
    // First, check if the game exists and is not already marked as completed or diverged
    const { data: gameData, error: gameCheckError } = await supabase
      .from("games")
      .select("status")
      .eq("id", gameId)
      .single();
      
    if (gameCheckError) {
      console.error("Error checking game status:", gameCheckError);
      return false;
    }
    
    if (!gameData || (gameData.status !== "in_progress")) {
      console.log("Game is already not in progress or doesn't exist:", gameData?.status);
      return false;
    }
    
    // Update the game status
    const { error } = await supabase
      .from("games")
      .update({ 
        status: "diverged" 
      })
      .eq("id", gameId);
      
    if (error) {
      console.error("Error in divergeGame SQL:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error marking game as diverged:", error);
    return false;
  }
};

/**
 * Submit user word for a round
 */
export const submitUserWord = async (gameId: string, roundNumber: number, word: string) => {
  try {
    // Ensure the word is trimmed before saving to the database
    const trimmedWord = word.trim();
    
    const { error } = await supabase
      .from("rounds")
      .update({ user_word: trimmedWord })
      .eq("game_id", gameId)
      .eq("round_number", roundNumber);
      
    if (error) throw error;
    
    // Get the bot's word for this round
    const { data, error: roundError } = await supabase
      .from("rounds")
      .select("ai_word, ai_explanation")
      .eq("game_id", gameId)
      .eq("round_number", roundNumber)
      .maybeSingle();
        
    if (roundError) throw roundError;
    
    return {
      aiWord: data?.ai_word || "",
      aiExplanation: data?.ai_explanation || undefined
    };
  } catch (error) {
    console.error("Error submitting user word:", error);
    throw error;
  }
};
